import React from 'react';
import clsx from 'clsx';
import {Icon, type IconProps, type IconDefinition} from '@netx/core-icons';
import {capitalize, Typography} from '@mui/material';
import type {PaletteColor} from '@mui/material/styles/createPalette';
import {
	composeClasses,
	SEVERITIES,
	Severity,
	styled,
	WithClasses,
} from '@netx/core-components/Theme';
import {
	FALLBACK_MESSAGE_CLASS_NAME,
	fallbackMessageClasses,
	FallbackMessageClasses,
	getFallbackMessageUtilityClass,
} from './fallbackMessageClasses';

type FallbackMessageUtiltiyClassProps = WithClasses<
	FallbackMessageClasses,
	{
		disablePadding?: boolean;
		severity?: Severity;
		variant?: 'default' | 'light';
	}
>;

const useUtilityClasses = (ownerState: FallbackMessageUtiltiyClassProps) => {
	const {classes, severity, variant} = ownerState;
	const slots = {
		root: ['root', variant, severity && `severity${capitalize(severity)}`],
		icon: ['icon', variant, severity && `severity${capitalize(severity)}`],
		text: ['text', variant, severity && `severity${capitalize(severity)}`],
		children: ['children'],
		body: ['body'],
		header: ['header', variant],
	};
	return composeClasses(slots, getFallbackMessageUtilityClass, classes);
};

const FallbackMessageRoot = styled('div', {
	name: FALLBACK_MESSAGE_CLASS_NAME,
	slot: 'Root',
})(({theme, ownerState, sx}) => ({
	alignItems: 'center',
	background: theme.palette.background.paper,
	flex: '1 1 auto',
	display: 'flex',
	flexDirection: 'column',
	justifyContent: 'center',
	minHeight: 0,
	overflow: 'hidden',
	textAlign: 'center',
	width: '100%',
	whiteSpace: 'normal',
	...(!ownerState.disablePadding && {padding: theme.spacing(3)}),
	...(ownerState?.iconColor && {
		[`& .${fallbackMessageClasses.icon}`]: {
			color: (
				theme.palette[
					ownerState.iconColor as keyof (typeof theme)['palette']
				] as PaletteColor
			).main,
		},
	}),
}));

const FallbackMessageIcon = styled(Icon, {
	name: FALLBACK_MESSAGE_CLASS_NAME,
	slot: 'Icon',
})(({theme}) => ({
	color: theme.palette.custom.icon.onBackground,
	// TODO: investigate why FA7 icons are not sized the same...
	// the svg reports a square image but if we do 75x75 then the top and bottom are cut off slightly
	// at least for the `faCog` icon - need to check if this is the case for all FA7 icons
	height: '80px !important',
	width: '75px !important',
	marginBottom: theme.spacing(2),
	[`&.${fallbackMessageClasses.light}`]: {
		...SEVERITIES.reduce((accum, key) => {
			return key === 'default'
				? accum
				: {
						...accum,
						[`&.${
							fallbackMessageClasses[
								`severity${capitalize(
									key,
								)}` as keyof typeof fallbackMessageClasses
							]
						}`]: {
							color: theme.palette[key].light,
						},
					};
		}, {}),
	},
	[`&:not(.${fallbackMessageClasses.light})`]: {
		...SEVERITIES.reduce((accum, key) => {
			return key === 'default'
				? accum
				: {
						...accum,
						[`&.${
							fallbackMessageClasses[
								`severity${capitalize(
									key,
								)}` as keyof typeof fallbackMessageClasses
							]
						}`]: {
							color: theme.palette[key].main,
						},
					};
		}, {}),
	},
}));

const FallbackMessageBody = styled('div', {
	name: FALLBACK_MESSAGE_CLASS_NAME,
	slot: 'Body',
})(({theme}) => ({
	display: 'flex',
	flexDirection: 'column',
	alignItems: 'center',
	justifyContent: 'center',
	color: theme.palette.text.secondary,
	width: '100%',
}));

const FallbackMessageHeader = styled('div', {
	name: FALLBACK_MESSAGE_CLASS_NAME,
	slot: 'Header',
})(({theme}) => ({
	[`&:not(.${fallbackMessageClasses.light}) .${fallbackMessageClasses.text}`]: {
		color: theme.palette.text.primary,
	},
}));

const FallbackMessageTitle = styled(Typography, {
	name: FALLBACK_MESSAGE_CLASS_NAME,
	slot: 'Text',
})(({theme}) => ({
	// [`&.${fallbackMessageClasses.light}`]: {
	// 	color: theme.palette.text.secondary,
	// },
}));

const FallbackMessageText = styled(Typography, {
	name: FALLBACK_MESSAGE_CLASS_NAME,
	slot: 'Text',
})(({theme}) => ({
	[`&.${fallbackMessageClasses.light}`]: {
		color: theme.palette.text.secondary,
	},
}));

const FallbackMessageChildren = styled('div', {
	name: FALLBACK_MESSAGE_CLASS_NAME,
	slot: 'Children',
})(({theme}) => ({
	display: 'flex',
	alignItems: 'center',
	justifyContent: 'center',
	width: '100%',
	'&:not(:empty)': {
		marginTop: theme.spacing(3),
	},
}));

export interface FallbackMessageProps
	extends
		React.DetailedHTMLProps<
			React.HTMLAttributes<HTMLDivElement>,
			HTMLDivElement
		>,
		React.PropsWithChildren,
		WithClasses<FallbackMessageClasses> {
	disablePadding?: boolean;
	icon?: IconDefinition;
	iconColor?:
		| 'info'
		| 'error'
		| 'warning'
		| 'success'
		| 'primary'
		| 'secondary';
	IconProps?: Partial<Omit<IconProps, 'className' | 'icon'>>;
	title?: string;
	enhancedTitle?: boolean;
	message?: string;
	severity?: Severity;
	variant?: 'default' | 'light';
}

/**
 * A uniformed way to show fallback messages in the app \
 * It will size to whatever container it is put in - it will not influence the size of the parent container
 */
export const FallbackMessage = ({
	classes: _classes,
	className,
	children,
	disablePadding,
	enhancedTitle,
	icon,
	iconColor,
	message,
	severity,
	title,
	variant = 'light',
	IconProps,
	...props
}: FallbackMessageProps) => {
	const classes = useUtilityClasses({
		classes: _classes,
		className,
		disablePadding,
		severity,
		variant,
	});

	const FallbackTitleComponent = enhancedTitle
		? FallbackMessageTitle
		: FallbackMessageText;

	const hasChildren =
		children != null &&
		(!Array.isArray(children) || (!!children.length && children.some(Boolean)));

	return (
		<FallbackMessageRoot
			className={clsx(classes.root, className)}
			ownerState={{disablePadding, iconColor}}
			{...props}
		>
			<FallbackMessageHeader className={classes.header}>
				{icon && (
					<FallbackMessageIcon
						className={classes.icon}
						icon={icon}
						{...IconProps}
					/>
				)}
				{title ? (
					<FallbackTitleComponent
						className={classes.text}
						variant="body1"
						gutterBottom
					>
						{title}
					</FallbackTitleComponent>
				) : null}
			</FallbackMessageHeader>
			{(message != null || hasChildren) && (
				<FallbackMessageBody className={classes.body}>
					{message != null && (
						<FallbackMessageText className={classes.text} variant="body2">
							{message}
						</FallbackMessageText>
					)}

					{children ? (
						<FallbackMessageChildren className={classes.children}>
							{children}
						</FallbackMessageChildren>
					) : null}
				</FallbackMessageBody>
			)}
		</FallbackMessageRoot>
	);
};

export default FallbackMessage;
